import { uiText } from "@/lib/i18n/uiText";
import { InstallAppButton } from "@/components/pwa/InstallAppButton";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "@/lib/router-compat";
import { cn } from "@/lib/utils";
import {
  LogIn,
  LogOut,
  Settings,
  Upload,
  ListChecks,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { authPath } from "@/lib/authRedirect";

import { NAV_GROUPS, NAV_ICONS, type NavLeaf } from "@/lib/navigation";
import { Logo } from "@/components/brand/Logo";
import { slugify } from "@/lib/entities";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { LanguageErrorBoundary } from "@/components/i18n/LanguageErrorBoundary";
import { TimezoneSelector } from "@/components/settings/TimezoneSelector";

interface AdminItem {
  title: string;
  url: string;
  icon: LucideIcon;
}

const adminNavItems: AdminItem[] = [
  { title: "Match Curation", url: "/match-curation", icon: ListChecks },
  { title: "Upload Data", url: "/upload", icon: Upload },
  { title: "Admin", url: "/admin", icon: Settings },
];

interface AppSidebarProps {
  /** Controlled open state for the mobile navigation drawer. */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

export function AppSidebar({ mobileOpen: controlledOpen, onMobileOpenChange }: AppSidebarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const mobileOpen = controlledOpen ?? uncontrolledOpen;
  const setMobileOpen = onMobileOpenChange ?? setUncontrolledOpen;
  const { user, isAdmin, signOut } = useAuth();


  // Collapsible groups — default every group open so all links are visible.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggle = (label: string) =>
    setCollapsed((c) => ({ ...c, [label]: !c[label] }));

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({ variant: "destructive", title: t("header.errorSigningOut"), description: error.message });
    } else {
      toast({ title: t("header.signedOut"), description: t("header.signedOutDesc") });
      navigate("/");
    }
    setMobileOpen(false);
  };

  const LeafLink = ({ item }: { item: NavLeaf }) => {
    const isActive = location.pathname === item.url;
    const Icon = NAV_ICONS[item.url];
    return (
      <NavLink
        to={item.url}
        end
        className={cn(
          "flex items-center gap-3 px-3 py-2 min-h-11 rounded-lg text-sm font-medium transition-all",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring",
          isActive
            ? "bg-sidebar-primary text-sidebar-primary-foreground"
            : "text-sidebar-foreground/70",
        )}
        activeClassName="bg-sidebar-primary text-sidebar-primary-foreground"
        onClick={() => setMobileOpen(false)}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="flex-1 truncate">{uiText(item.title)}</span>
      </NavLink>
    );
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-sidebar-border">
        <Logo variant="full" to="/" imgClassName="h-10" />
      </div>

      {/* Navigation — config-driven collapsible groups */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2" aria-label={uiText("Main navigation")}>
        {NAV_GROUPS.map((group) => {
          const isCollapsed = collapsed[group.label];
          const GroupIcon = group.icon;
          const panelId = `nav-group-${slugify(group.label)}`;
          const buttonId = `nav-group-btn-${slugify(group.label)}`;
          return (
            <div key={group.label}>
              <button
                type="button"
                id={buttonId}
                onClick={() => toggle(group.label)}
                className="min-h-11 w-full flex items-center gap-2 px-3 mb-1 rounded-lg text-xs font-semibold text-sidebar-foreground/60 uppercase tracking-wider hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring"
                aria-expanded={!isCollapsed}
                aria-controls={panelId}
              >
                <GroupIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 text-left">{uiText(group.label)}</span>
                <ChevronDown
                  className={cn("h-3.5 w-3.5 transition-transform", isCollapsed && "-rotate-90")}
                />
              </button>
              <div id={panelId} role="region" aria-labelledby={buttonId} hidden={isCollapsed}>
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <LeafLink key={`${group.label}-${item.url}-${item.title}`} item={item} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}


        {/* Admin navigation items - only show if admin */}
        {isAdmin && (
          <>
            <div className="my-4 border-t border-sidebar-border" />
            <p className="px-3 mb-2 text-xs font-medium text-sidebar-foreground/50 uppercase tracking-wider">
               {uiText("Admin")} </p>
            {adminNavItems.map((item) => {
              const isActive = location.pathname === item.url;
              return (
                <NavLink
                  key={item.url}
                  to={item.url}
                  end
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    isActive
                      ? "bg-sidebar-primary text-sidebar-primary-foreground"
                      : "text-sidebar-foreground/70",
                  )}
                  activeClassName="bg-sidebar-primary text-sidebar-primary-foreground"
                  onClick={() => setMobileOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{uiText(item.title)}</span>
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      {/* Footer with Auth */}
      <div className="p-4 border-t border-sidebar-border space-y-3">
        {user ? (
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-sidebar-foreground/70 hover:text-sidebar-foreground"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4" />
            {t("header.signOut")}
          </Button>
        ) : (
          <NavLink
            to={authPath(location)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all"
            onClick={() => setMobileOpen(false)}
          >
            <LogIn className="h-4 w-4" />
            {t("header.signIn")}
          </NavLink>
        )}
        {/* Language + timezone: hidden from the top bar below `md`, so the
            drawer is where phone visitors reach them. */}
        <div className="flex md:hidden items-center justify-center gap-2">
          <LanguageErrorBoundary>
            <LanguageSwitcher />
          </LanguageErrorBoundary>
          <TimezoneSelector variant="compact" />
        </div>
        <InstallAppButton className="w-full min-h-11" />
        <p className="text-xs text-sidebar-foreground/50 text-center">
          {t("footer.tagline")}
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-sidebar-border bg-sidebar sticky top-0 h-screen">
        <NavContent />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="p-0 w-72 bg-sidebar border-sidebar-border overflow-y-auto"
          aria-label={uiText("Site navigation")}
        >
          <SheetTitle className="sr-only">{uiText("Statlign navigation")}</SheetTitle>
          <SheetDescription className="sr-only">
             {uiText("Browse predictions, sports, leagues, markets and site pages.")} </SheetDescription>
          <NavContent />
        </SheetContent>
      </Sheet>

    </>
  );
}
